"use client";
import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../../context/AppContext";
import { useVisual } from "../../context/VisualContext";
import { useUser } from "../../context/UserContext";
import { useHandleStreamResponse, useVisualEffects } from "../../hooks/useEffects";

const ParticleSystem = ({ density = "medium", interactive = true, colorScheme = "nebula" }) => {
  const canvasRef = useRef(null);
  const { settings } = useVisual();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: null, y: null });
  const animationRef = useRef(null);
  
  // Get particle count based on density
  const getParticleCount = () => {
    const densityMap = {
      low: 50,
      medium: 100,
      high: 200
    };
    return densityMap[density] || densityMap.medium;
  };
  
  // Get color scheme
  const getColorScheme = () => {
    const schemes = {
      nebula: ["#4A90E2", "#ff00ea", "#bd00ff", "#00c2ff"],
      cosmic: ["#ff3366", "#ff6633", "#ffcc33", "#33ccff"],
      aurora: ["#4CAF50", "#FFD700", "#00BCD4", "#9C27B0"]
    };
    return schemes[colorScheme] || schemes.nebula;
  };
  
  // Initialize particles
  const initParticles = () => {
    if (!canvasRef.current) return;
    
    const { width, height } = dimensions;
    const count = getParticleCount();
    const colors = getColorScheme();
    
    particlesRef.current = Array.from({ length: count }, () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      radius: Math.random() * 3 + 1,
      color: colors[Math.floor(Math.random() * colors.length)],
      vx: Math.random() * 1 - 0.5,
      vy: Math.random() * 1 - 0.5,
      opacity: Math.random() * 0.5 + 0.5
    }));
  };
  
  // Draw particles
  const drawParticles = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { width, height } = dimensions;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Draw particles
    particlesRef.current.forEach(particle => {
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
      ctx.fillStyle = particle.color;
      ctx.globalAlpha = particle.opacity;
      ctx.fill();
    });
    
    // Draw connections
    if (interactive && mouseRef.current.x !== null && mouseRef.current.y !== null) {
      particlesRef.current.forEach(particle => {
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(mouseRef.current.x, mouseRef.current.y);
          ctx.strokeStyle = particle.color;
          ctx.globalAlpha = 0.2 * (1 - distance / 100);
          ctx.stroke();
        }
      });
    }
    
    // Reset global alpha
    ctx.globalAlpha = 1;
  };
  
  // Update particles
  const updateParticles = () => {
    if (!canvasRef.current) return;
    
    const { width, height } = dimensions;
    
    particlesRef.current.forEach(particle => {
      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;
      
      // Bounce off edges
      if (particle.x < 0 || particle.x > width) {
        particle.vx = -particle.vx;
      }
      
      if (particle.y < 0 || particle.y > height) {
        particle.vy = -particle.vy;
      }
      
      // Mouse interaction
      if (interactive && mouseRef.current.x !== null && mouseRef.current.y !== null) {
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          const force = 0.5 / distance;
          particle.vx += dx * force;
          particle.vy += dy * force;
        }
      }
      
      // Limit velocity
      const speed = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy);
      if (speed > 2) {
        particle.vx = (particle.vx / speed) * 2;
        particle.vy = (particle.vy / speed) * 2;
      }
    });
  };
  
  // Animation loop
  const animate = () => {
    updateParticles();
    drawParticles();
    animationRef.current = requestAnimationFrame(animate);
  };
  
  // Handle mouse move
  const handleMouseMove = (e) => {
    if (!interactive) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    mouseRef.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };
  
  // Handle mouse leave
  const handleMouseLeave = () => {
    mouseRef.current = { x: null, y: null };
  };
  
  // Handle resize
  const handleResize = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const parent = canvas.parentElement;
    
    const newWidth = parent.clientWidth;
    const newHeight = parent.clientHeight;
    
    canvas.width = newWidth;
    canvas.height = newHeight;
    
    setDimensions({ width: newWidth, height: newHeight });
  };
  
  // Initialize canvas and particles
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const parent = canvas.parentElement;
    
    const width = parent.clientWidth;
    const height = parent.clientHeight;
    
    canvas.width = width;
    canvas.height = height;
    
    setDimensions({ width, height });
    
    // Handle resize
    window.addEventListener("resize", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  
  // Initialize particles when dimensions change
  useEffect(() => {
    if (dimensions.width > 0 && dimensions.height > 0) {
      initParticles();
    }
  }, [dimensions, density, colorScheme]);
  
  // Start animation
  useEffect(() => {
    if (dimensions.width > 0 && dimensions.height > 0) {
      animationRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dimensions, interactive, colorScheme]);
  
  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    />
  );
};

export default ParticleSystem;
